/**
 * Runtime de armas (doc 03 §5, mira doc 07 §9). Modelo contínuo e determinístico:
 * cada arma aplica dano proporcional a dps·dt no(s) alvo(s) da sua regra de mira.
 * Sem projéteis discretos na sim (A2) — o cliente renderiza os disparos por cima.
 */
import { WEAPONS, type Weapon } from '../data/index.ts';
import { cosT, sinT } from './sintab.ts';
import type { EnemySim, HeroSim, RunState } from './types.ts';
import { TICKS_POR_SEGUNDO } from './types.ts';

const DT = 1 / TICKS_POR_SEGUNDO;
const byId = new Map(WEAPONS.map((w) => [w.id, w]));

export function weaponDef(id: string): Weapon | undefined {
  return byId.get(id);
}

/** DPS efetivo por nível (interp linear nv1..nv5); evoluída = dpsNv5·mult */
export function weaponDps(w: Weapon, level: number, evolved: boolean): number {
  if (evolved) return w.dpsNv5 * w.dpsEvolucaoMult;
  const t = (Math.max(1, Math.min(5, level)) - 1) / 4;
  return w.dpsNv1 + (w.dpsNv5 - w.dpsNv1) * t;
}

const AURA_RAIO = 110;
const ORBITAL_RAIO = 96;
const ORBITAL_ESPESSURA = 28;

/** aplica o dano de todas as armas do herói neste tick; retorna kills causados.
 *  danoMul = bônus do Ferreiro (Sutarifu); 1 = sim base. */
export function applyWeapons(state: RunState, hero: HeroSim, danoMul = 1): number {
  let kills = 0;
  for (const ow of hero.weapons) {
    const def = byId.get(ow.id);
    if (!def) continue;
    const dps = weaponDps(def, ow.level, ow.evolved);
    const dmg = dps * DT * danoMul;
    kills += applyByMira(state, hero, def, dmg);
  }
  return kills;
}

function applyByMira(state: RunState, hero: HeroSim, def: Weapon, dmg: number): number {
  switch (def.mira) {
    case 'aura':
      return damageInRadius(state, hero, hero.x, hero.y, AURA_RAIO, dmg);
    case 'orbital': {
      // dano num anel ao redor do herói (a "página"/floco girando)
      let k = 0;
      for (const e of state.enemies) {
        if (!e.alive) continue;
        const d = dist(e.x, e.y, hero.x, hero.y);
        if (Math.abs(d - ORBITAL_RAIO) <= ORBITAL_ESPESSURA) k += hit(e, dmg, state, hero);
      }
      return k;
    }
    case 'zona_densa':
      return damageInRadius(state, hero, hero.x, hero.y, AURA_RAIO * 1.2, dmg * 0.8);
    case 'espiral':
      return damageInRadius(state, hero, hero.x, hero.y, ORBITAL_RAIO, dmg);
    default: {
      // nearest / nearest_curto / movimento / propria / aleatorio → single-target no mais próximo
      const alvo = nearestEnemy(state, hero.x, hero.y);
      if (!alvo) return 0;
      return hit(alvo, dmg, state, hero);
    }
  }
}

function damageInRadius(state: RunState, hero: HeroSim, x: number, y: number, r: number, dmg: number): number {
  let k = 0;
  const r2 = r * r;
  for (const e of state.enemies) {
    if (!e.alive) continue;
    if (dist2(e.x, e.y, x, y) <= r2) k += hit(e, dmg, state, hero);
  }
  return k;
}

function hit(e: EnemySim, dmg: number, state: RunState, hero: HeroSim): number {
  e.hp -= dmg;
  if (e.hp <= 0) {
    e.alive = false;
    hero.kills += 1;
    state.stats.kills += 1;
    return 1;
  }
  return 0;
}

/** órbita visual determinística (para o cliente): posição das armas orbitais */
export function orbitPoint(hero: HeroSim, idx: number, total: number): { x: number; y: number } {
  const a = hero.orbitPhase + idx / Math.max(1, total);
  return { x: hero.x + cosT(a) * ORBITAL_RAIO, y: hero.y + sinT(a) * ORBITAL_RAIO };
}

function nearestEnemy(state: RunState, x: number, y: number): EnemySim | null {
  let best: EnemySim | null = null;
  let bd = Infinity;
  for (const e of state.enemies) {
    if (!e.alive) continue;
    const d = dist2(x, y, e.x, e.y);
    if (d < bd) {
      bd = d;
      best = e;
    }
  }
  return best;
}

const dist2 = (ax: number, ay: number, bx: number, by: number): number =>
  (ax - bx) * (ax - bx) + (ay - by) * (ay - by);
const dist = (ax: number, ay: number, bx: number, by: number): number => Math.sqrt(dist2(ax, ay, bx, by));
